import { cn } from '@repo/tailwind-config/tailwind.functions'
import { BalloonIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getCenterActivities } from '../../../../api/centers/center-activities/get-center-activities'
import { updateCenterActivity } from '../../../../api/centers/center-activities/update-center-activity'
import { getCenter } from '../../../../api/centers/get-center'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { ActivityMemberEnum } from '../../../../lib/types/enums/activity-member/activity-member.enum'
import { Center } from '../../../../lib/types/models/center/center'
import { CenterActivity } from '../../../../lib/types/models/center-activity/center-activity'
import { CenterActivityInput } from '../../../../lib/types/models/center-activity/center-activity-input'
import { DataProps } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import EditableTable, { EditableTableColumn } from '../../../elements/editable-table'
import { useCenterCosts } from './center.hooks'
import {
  centerCostsTableColorMapping,
  CenterCostsTableFooter,
  CenterCostsTableHeader,
  CenterTableSkeleton,
  UploadInvoiceComponent,
  userInvoiceColorMapping
} from './center.ui'
import {
  centerActivitiesEditableColumns,
  CenterCostsTableProps,
  getCenterActivitiesColumns,
  mapCenterActivityCostToInput
} from './utils'

type CenterActivityCostInput = CenterActivityInput & { id: number }

export function CenterActivitiesTable(props: DataProps<Center, CenterCostsTableProps>) {
  const { costType, mode, currency } = props
  const { t } = useTranslate()
  const { user } = useAuth()

  const [editable, setEditable] = useState(false)
  const [activities, setActivities] = useState<CenterActivityCostInput[]>()

  const {
    id: centerId,
    data: center,
    refetch: refetchCenter
  } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { centerCostsView, isPending, uploadInvoice, deleteInvoice, lock, unlock } =
    useCenterCosts({
      centerId,
      costType,
      table: 'activities'
    })

  const {
    data: centerActivities,
    isPending: isPendingCenterActivities,
    refetch
  } = useQuery({
    queryKey: ['center-activities', centerId],
    queryFn: () =>
      getCenterActivities(centerId, { filter: { paidBy: [ActivityMemberEnum.ORGANIZER] } })
  })

  const { mutateAsync, isPending: isUpdatingCenterActivityPending } = useMutation({
    mutationFn: updateCenterActivity,
    onError: showAxiosError,
    onSuccess: () => setActivities(undefined)
  })

  const totalLocalOrganizerCost = centerCostsView.data?.totalLocalOrganizerCost ?? 0
  const finalTotalLocalOrganizerCost = centerCostsView.data?.finalTotalLocalOrganizerCost ?? 0
  const localOrganizerBalance = finalTotalLocalOrganizerCost - totalLocalOrganizerCost

  const columns: EditableTableColumn<CenterActivity>[] = [
    ...getCenterActivitiesColumns(
      costType,
      currency,
      t,
      centerActivitiesEditableColumns[user.role]
    ),
    ...(costType === 'final-cost'
      ? ([
          {
            accessorKey: 'invoices',
            header: t('center.costs.tefl.teachers.and.leaders.table.header.upload.invoices'),
            centerCell: true,
            cell: ({ row }) => (
              <UploadInvoiceComponent
                invoice={row.original.invoice}
                variant={userInvoiceColorMapping[user.role]}
                readOnly={(!editable && mode === 'action-edit') || mode === 'readonly'}
                disabled={isPending}
                onDelete={async () => {
                  await deleteInvoice.mutateAsync(row.original.id)
                  refetch()
                }}
                onChange={async (file) => {
                  await uploadInvoice.mutateAsync({ id: row.original.id, file })
                  refetch()
                }}
              />
            )
          }
        ] as EditableTableColumn<CenterActivity>[])
      : [])
  ]

  if (!center) return <CenterTableSkeleton />
  return (
    <div>
      <CenterCostsTableHeader
        icon={BalloonIcon}
        title={t('center.costs.activity.budget.title')}
        mode={mode}
        onCancel={() => setActivities(undefined)}
        onEditableChange={setEditable}
        validateOptions={{
          validate: costType === 'final-cost',
          locked: center.isActivitiesLocked,
          onLock: lock.mutateAsync,
          onUnlock: unlock.mutateAsync
        }}
        onSave={async () => {
          if (activities) {
            for (const activity of activities) await mutateAsync({ ...activity, centerId })
            await Promise.all([refetch(), centerCostsView.refetch(), refetchCenter?.()])
          }
        }}
      />

      <EditableTable<CenterActivity>
        isEditable={editable}
        data={centerActivities?.data}
        isLoading={isPendingCenterActivities || isUpdatingCenterActivityPending}
        columns={columns}
        manualSorting={false}
        footerRowClassName={cn({ 'bg-white': costType === 'final-cost' })}
        onDataChange={(data) => {
          const mapper = data.map((item) => {
            const mappedItem = mapCenterActivityCostToInput(item, costType)
            return { ...mappedItem, id: item.id }
          })
          if (mode === 'free-edit') for (const item of mapper) mutateAsync({ ...item, centerId })
          else setActivities(mapper)
        }}
        footer={
          <CenterCostsTableFooter
            currency={currency}
            costType={costType}
            textColor={centerCostsTableColorMapping[user.role][costType].textColor}
            variant={centerCostsTableColorMapping[user.role][costType].variant}
            label={
              costType === 'final-cost'
                ? t('center.final.costs.fees')
                : t(
                    'center.costs.activity.budget.table.footer.total.activity.budget.paid.by.local.organiser'
                  )
            }
            total={totalLocalOrganizerCost}
            initialAmount={totalLocalOrganizerCost}
            finalAmount={finalTotalLocalOrganizerCost}
            balance={localOrganizerBalance}
          />
        }
      />
    </div>
  )
}
